import React from 'react';

// Типы для кнопок
export interface ModalButton {
    type:
        | 'primary'
        | 'secondary'
        | 'success'
        | 'warning'
        | 'danger'
        | 'ghost'
        | 'info'
        | 'blue'
        | 'green'
        | 'gray'
        | 'red';
    text: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    onClickAsync?: () => Promise<void>;
    isLoading?: boolean;
    disabled?: boolean;
}

// Новый интерфейс для простых модалок
export interface SimpleModalData {
    id: string;
    isOpen: boolean;
    title: string;
    message?: string;
    content?: React.ReactNode; // Кастомный контент вместо message
    buttons?: ModalButton[];
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    maxWidth?: string;
    maxHeight?: string;
    onClose?: () => void; // Общий колбэк для закрытия модалки
    isCloseDisabled?: boolean; // Если true, модалку нельзя закрыть по крестику и клику снаружи
}

// Старый интерфейс для обратной совместимости
export interface ModalData {
    id: string;
    isOpen: boolean;
    header: React.ReactNode;
    body: React.ReactNode;
    footer: React.ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
    maxWidth?: string;
    maxHeight?: string;
}

type ModalCallback = (modals: SimpleModalData[]) => void;

class ModalManager {
    private static instance: ModalManager;
    private modals: SimpleModalData[] = [];
    private subscribers: ModalCallback[] = [];
    private nextId = 1;
    private isBlocked = false;

    private constructor() {}

    static getInstance(): ModalManager {
        if (!ModalManager.instance) {
            ModalManager.instance = new ModalManager();
        }
        return ModalManager.instance;
    }

    subscribe(callback: ModalCallback): () => void {
        this.subscribers.push(callback);

        // Возвращаем функцию для отписки
        return () => {
            const index = this.subscribers.indexOf(callback);
            if (index > -1) {
                this.subscribers.splice(index, 1);
            }
        };
    }

    private notifySubscribers(): void {
        this.subscribers.forEach((callback) => callback([...this.modals]));
    }

    // Методы для управления блокировкой модалок
    blockModals(): void {
        this.isBlocked = true;
    }

    unblockModals(): void {
        this.isBlocked = false;
    }

    isModalsBlocked(): boolean {
        return this.isBlocked;
    }

    /**
     * Показывает модалку или обновляет существующую
     * @param config - конфигурация модалки
     * @param ref - опциональный ref для отслеживания ID модалки
     * @returns ID модалки
     */
    show(config: Omit<SimpleModalData, 'id' | 'isOpen'>, ref?: { current: string }): string {
        // Если передан ref и он не пустой, обновляем существующую модалку
        if (ref && ref.current) {
            this.updateModal(ref.current, config);
            return ref.current;
        }

        // Если модалки заблокированы, не показываем новую
        if (this.isBlocked) {
            console.warn('Modals are blocked, cannot show new modal');
            return '';
        }

        // Создаем новую модалку
        const id = `modal-${this.nextId++}`;
        const modal: SimpleModalData = {
            ...config,
            id,
            isOpen: true,
        };

        // Скрываем все предыдущие модалки
        this.modals = this.modals.map((prevModal) => ({ ...prevModal, isOpen: false }));

        // Добавляем новую модалку
        this.modals.push(modal);
        this.notifySubscribers();

        // Если передан ref, сохраняем ID
        if (ref) {
            ref.current = id;
        }

        return id;
    }

    hide(id?: string): void {
        if (id) {
            const modal = this.modals.find((m) => m.id === id);
            if (modal?.onClose) {
                modal.onClose();
            }
            this.modals = this.modals.map((modal) => (modal.id === id ? { ...modal, isOpen: false } : modal));
        } else {
            // Скрываем последнюю модалку
            if (this.modals.length > 0) {
                const lastModal = this.modals[this.modals.length - 1];
                if (lastModal.onClose) {
                    lastModal.onClose();
                }
                this.modals = this.modals.map((modal, index) =>
                    index === this.modals.length - 1 ? { ...modal, isOpen: false } : modal,
                );
            }
        }
        this.notifySubscribers();
    }

    remove(id?: string): void {
        if (id) {
            const modal = this.modals.find((m) => m.id === id);
            if (modal?.onClose) {
                modal.onClose();
            }
            this.modals = this.modals.filter((modal) => modal.id !== id);
            // Если после удаления остались модалки, показываем последнюю
            if (this.modals.length > 0) {
                this.modals = this.modals.map((modal, index) =>
                    index === this.modals.length - 1
                        ? { ...modal, isOpen: true }
                        : { ...modal, isOpen: false },
                );
            }
        } else {
            // Удаляем все модалки и вызываем onClose для последней
            if (this.modals.length > 0) {
                const lastModal = this.modals[this.modals.length - 1];
                if (lastModal.onClose) {
                    lastModal.onClose();
                }
            }
            this.modals = [];
        }
        this.notifySubscribers();
    }

    updateModal(id: string, updates: Partial<Omit<SimpleModalData, 'id' | 'isOpen'>>): void {
        const modalIndex = this.modals.findIndex((modal) => modal.id === id);
        if (modalIndex !== -1) {
            this.modals[modalIndex] = {
                ...this.modals[modalIndex],
                ...updates,
            };
            this.notifySubscribers();
        }
    }

    getModals(): SimpleModalData[] {
        return [...this.modals];
    }

    // Удобные методы для быстрого создания модалок
    confirm(
        title: string,
        message: string,
        onConfirm: () => void,
        onCancel?: () => void,
        confirmText = 'Confirm',
        cancelText = 'Cancel',
        onClose?: () => void,
        ref?: { current: string },
    ): string {
        const buttons: ModalButton[] = [
            {
                type: 'secondary',
                text: cancelText,
                onClick: () => {
                    onCancel?.();
                    this.remove();
                },
            },
            {
                type: 'primary',
                text: confirmText,
                onClick: () => {
                    onConfirm();
                    this.remove();
                },
            },
        ];

        return this.show(
            {
                title,
                message,
                buttons,
                size: 'sm',
                onClose,
            },
            ref,
        );
    }

    alert(
        title: string,
        message: string,
        buttonText = 'OK',
        onClose?: () => void,
        ref?: { current: string },
    ): string {
        const buttons: ModalButton[] = [
            {
                type: 'primary',
                text: buttonText,
                onClick: () => this.remove(),
            },
        ];

        return this.show(
            {
                title,
                message,
                buttons,
                size: 'sm',
                onClose,
            },
            ref,
        );
    }
}

// Экспортируем синглтон
export const modalManager = ModalManager.getInstance();

// Экспортируем класс для тестирования
export { ModalManager };
